import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CanceledError, InternalAxiosRequestConfig,
  ParamsSerializerOptions,
} from 'axios';
import Cookies from 'js-cookie';
import LoginEvent, { NeedLogin } from '@/events/login-event';
import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import qs from 'qs';
import { message } from '@/utils';

declare class Request {
  request<T = any, D = any>(config: AxiosRequestConfig<D>): Promise<T>;

  get<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<T>;

  delete<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<T>;

  head<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<T>;

  options<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<T>;

  post<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<T>;

  put<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<T>;

  patch<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<T>;

  postForm<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<T>;

  putForm<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<T>;

  patchForm<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<T>;
}

const instance = axios.create();

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const csrfToken = Cookies.get('insurance_cms_csrf_token');
  config.headers.set('X-Requested-With', 'XMLHttpRequest');
  config.headers.set('X-XSRF-TOKEN', csrfToken);
  if (!config.timeout) {
    config.timeout = 300000;
  }
  config.withCredentials = true;
  return config;
};

const onRequestError = async <T, D>(error: AxiosError<T, D>): Promise<AxiosError<T, D>> => {
  return Promise.reject(error);
};

const onResponse = <T, D>(response: AxiosResponse<API.BaseResponse<T>, D>): T => {
  return response.data.data;
};

const onResponseError = async <T, D>(error: AxiosError<API.BaseResponse<T>, D>): Promise<T> => {
  if (error.response?.status === 401) {
    LoginEvent.emit(NeedLogin);
    throw new Error('Unauthorized');
  }
  if (!(error instanceof CanceledError)) {
    let msg = error.response?.data?.message;
    if (!msg) {
      msg = '未知错误';
    }
    if (!error?.config?.url?.includes('_DO_NOT_SHOW_ERROR_=true')) {
      void message.error(msg).then();
    }
  }
  return Promise.reject(error.response?.data);
};

// 请求拦截器
instance.interceptors.request.use(onRequest, onRequestError);

// 响应拦截器
instance.interceptors.response.use(onResponse, onResponseError);

const requestInner = instance as Request;
export const axiosRequest = requestInner;

type RequestConfig<D> = Omit<AxiosRequestConfig<D>, 'cancelToken'>;

/**
 * query参数数组格式化
 * 将a[]=1&a[]=2&a[]=3改为a=1&a=2&a=3
 */
const paramsSerializer: ParamsSerializerOptions = {
  serialize: params => {
    return qs.stringify(params, { indices: false });
  },
};

class RequestObservable {
  request<T = any, D = any>(config: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.request<T>(newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  get<T = any, D = any>(url: string, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.get<T>(url, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  delete<T = any, D = any>(url: string, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.delete<T>(url, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  head<T = any, D = any>(url: string, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.head<T>(url, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  options<T = any, D = any>(url: string, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.options<T>(url, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  post<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.post<T>(url, data, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  put<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.put<T>(url, data, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  patch<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.patch<T>(url, data, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  postForm<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.postForm<T>(url, data, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  putForm<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.putForm<T>(url, data, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }

  patchForm<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Observable<T> {
    return new Observable<T>(subscriber => {
      const source = axios.CancelToken.source();
      const newConfig: AxiosRequestConfig<D> = {
        paramsSerializer,
        cancelToken: source.token,
        ...config,
      };
      fromPromise(requestInner.patchForm<T>(url, data, newConfig)).subscribe(subscriber);
      return () => {
        source.cancel();
      };
    });
  }
}

const request = new RequestObservable();

export default request;
